import React, { Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

import UserSessionTimeoutMessage from './UserSessionTimeoutMessage';
import { baseTheme } from '../styles';
import { BrowserSuggestion } from './BrowserSuggestion';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { FullPageLoader } from '../../common/components';
import { isArabicLocale } from '../../common/utils/checkLocalePlayer';
import { useTheme } from '@mui/material';
import { RouteProps, useLocation } from 'react-router-dom';
import { SessionStore } from '../../constants/sessionStore';
import { SHLApp } from '../../constants/shlapp';
import { useAxiosInterceptor } from '../../common/utils/interceptor';
import { useTranslation } from 'react-i18next';
import '../styles/base.css';
import { useAppSelector } from '../../store/hook';
import { useCookieBotScript } from '../../common/hooks/useCookieBotScript';
import { useAccessibility } from './../../common/hooks/useAccessibility';
import { storeProvider } from '../../store/providers/storeProvider';
import { routeMappings } from '../../constants';
import GenericErrorMessage from '../../common/components/GenericErrorMessage';
import { getLocaleMapping } from '../utils/localeMapping';
import 'what-input';
import { useExternalCSS } from '../../common/hooks/useExternalCSS';
import { fonts } from '../../constants/fonts';
import { useDelayedFocus } from '../hooks/useDelayedFocus';
import { ONE_SECOND } from '../../constants/timers';
const Content = React.lazy(() => import('./Content'));
const Header = React.lazy(() => import('./Header'));
const Footer = React.lazy(() => import('./Footer'));
const AppPopup = React.lazy(() => import('../../shlApp/components/AppPopup'));
const PostHireFooter = React.lazy(() => import('./PostHireFooter'));

// Configure JSS
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: isArabicLocale() ? [rtlPlugin] : [],
});
/** Base Component for initializing theme having header content and footer */
const Base: React.FC<RouteProps> = (props: RouteProps): React.JSX.Element => {
  const template = useAppSelector((state) => state.template);
  const { isUserInactive } = useAppSelector((state) => state.basicDetails);
  const { resetStore } = storeProvider();
  const mainContentRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  /** Calling the axios intercepter hook */
  useAxiosInterceptor();

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const userAgentBool = navigator.userAgent.indexOf(SHLApp.IS_SHLAPP_AGENT) === -1;
  const isProceedToBrowser = sessionStorage.getItem(SessionStore.proceedToBrowser) === 'true';
  const rootElement = document.getElementById('root') as HTMLElement;

  /** Calling custom hook to focus on the main container on every location change or tab change */
  useDelayedFocus(rootElement, ONE_SECOND);

  const { t } = useTranslation();

  /** setting direction attribute on html/body */
  useLayoutEffect(() => {
    const langLocale = getLocaleMapping();

    const htmlTag = document.getElementsByTagName('html')[0];
    const bodyTag = document.getElementsByTagName('body')[0];
    const direction = isArabicLocale() ? 'rtl' : 'ltr';
    htmlTag.setAttribute('dir', direction);
    bodyTag.setAttribute('dir', direction);
    htmlTag.setAttribute('lang', langLocale);
    // remove dependency of template.settings to avoid useLayoutEffect execution on language change (CAW-6070)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useExternalCSS(fonts[template.settings.page.fontType]);

  /** calling create cookie bot script hook */
  useCookieBotScript();

  /* update accessibility for tcplus basicflow as per selection */
  useAccessibility();

  useEffect(() => {
    // only for tcflow
    if (location.pathname.includes(routeMappings['INIT_EXPERIENCE'])) {
      resetStore();
      const isUserLoggedOut = sessionStorage.getItem(SessionStore.loggedoutuser) || '';
      sessionStorage.clear();
      sessionStorage.setItem(SessionStore.loggedoutuser, isUserLoggedOut);
      localStorage.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={baseTheme(template)}>
        <CssBaseline />
        <Suspense fallback={<FullPageLoader isLoading />}>
          <Header mainContentRef={mainContentRef} />
          {!isProceedToBrowser && !template.settings.mfsEnabled && smDown && userAgentBool && <AppPopup />}
          {isUserInactive ? <UserSessionTimeoutMessage /> : <BrowserSuggestion />}
          <Content mainContentRef={mainContentRef}>{props.children}</Content>
          <GenericErrorMessage />
          {template.settings.mfsEnabled ? <PostHireFooter /> : <Footer />}
        </Suspense>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default Base;
